import { LocalizedDatePipe } from '../../../shared/pipe/localized-date.pipe';
import { CommonModule, DecimalPipe } from '@angular/common';
import {
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterModule } from '@angular/router';

import { DataStateComponent } from '../../../shared/components/ui/data-state/data-state.component';
import { PageHeaderComponent } from '../../../shared/components/ui/page-header/page-header.component';
import { toErrorMessage } from '../../../shared/helpers/http-error';
import { WalletsService } from '../../../shared/services/wallets.service';
import {
  ACCOUNT_STATUS_LABEL,
  ACCOUNT_TYPE_LABEL,
  AccountType,
  LEDGER_DIRECTION_LABEL,
  LedgerEntryResponse,
  OWNER_TYPE_LABEL,
  OwnerType,
  WalletSnapshotResponse,
} from '../../../shared/types/wallet.types';

@Component({
  selector: 'app-wallet-detail',
  imports: [
    CommonModule,
    RouterModule,
    LocalizedDatePipe,
    DecimalPipe,
    PageHeaderComponent,
    DataStateComponent,
  ],
  templateUrl: './wallet-detail.component.html',
})
export class WalletDetailComponent implements OnInit {
  private readonly service = inject(WalletsService);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);

  readonly snapshot = signal<WalletSnapshotResponse | null>(null);
  readonly loading = signal(true);
  readonly loadError = signal<string | null>(null);

  // Ledger
  readonly ledgerType = signal<AccountType>('CREDIT');
  readonly ledger = signal<LedgerEntryResponse[]>([]);
  readonly ledgerLoading = signal(false);
  readonly ledgerError = signal<string | null>(null);
  readonly ledgerPage = signal(1);
  readonly ledgerTotalPages = signal(0);
  readonly ledgerTotalItems = signal(0);
  readonly ledgerSize = 20;

  readonly typeLabel = OWNER_TYPE_LABEL;
  readonly accountTypeLabel = ACCOUNT_TYPE_LABEL;
  readonly accountStatusLabel = ACCOUNT_STATUS_LABEL;
  readonly directionLabel = LEDGER_DIRECTION_LABEL;

  ownerType!: OwnerType;
  ownerRef!: number;

  /** Link back to the actor entity page when navigable. */
  readonly actorLink = computed<unknown[] | null>(() => {
    if (this.ownerType === 'OWNER') return ['/owners', this.ownerRef];
    // Operator / driver — pages not built yet, no deep link.
    return null;
  });

  readonly ledgerPageWindow = computed<number[]>(() => {
    const total = this.ledgerTotalPages();
    if (total <= 1) return total === 1 ? [1] : [];
    const current = this.ledgerPage();
    const window = 5;
    let start = Math.max(1, current - Math.floor(window / 2));
    const end = Math.min(total, start + window - 1);
    start = Math.max(1, end - window + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  });

  ngOnInit(): void {
    const otParam = this.route.snapshot.paramMap.get('ownerType');
    const refParam = this.route.snapshot.paramMap.get('ownerRef');
    const parsedRef = refParam ? Number(refParam) : NaN;
    if (
      !otParam ||
      !['OWNER', 'OPERATOR', 'DRIVER', 'PLATFORM'].includes(otParam) ||
      !Number.isFinite(parsedRef) ||
        (parsedRef <= 0 && otParam !== 'PLATFORM')
    ) {
      this.loadError.set('Paramètres invalides.');
      this.loading.set(false);
      return;
    }
    this.ownerType = otParam as OwnerType;
    this.ownerRef = parsedRef;
    this.fetch();
  }

  fetch(): void {
    this.loading.set(true);
    this.loadError.set(null);
    this.service
      .getSnapshot(this.ownerType, this.ownerRef)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (snap) => {
          this.snapshot.set(snap);
          this.loading.set(false);
          // Default ledger tab — CREDIT — auto-load.
          this.loadLedger();
        },
        error: (err: unknown) => {
          this.loading.set(false);
          this.loadError.set(
            toErrorMessage(err, 'Impossible de charger ce wallet.', {
              404: 'Aucun wallet trouvé pour cet acteur (provisioning manquant ?).',
            }),
          );
        },
      });
  }

  switchLedger(type: AccountType): void {
    if (this.ledgerType() === type) return;
    this.ledgerType.set(type);
    this.ledgerPage.set(1);
    this.loadLedger();
  }

  loadLedger(): void {
    const snap = this.snapshot();
    if (!snap) return;
    const accountId =
      this.ledgerType() === 'CREDIT' ? snap.credit.id : snap.balance.id;
    this.ledgerLoading.set(true);
    this.ledgerError.set(null);
    this.service
      .ledgerByAccount(accountId, this.ledgerPage(), this.ledgerSize)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.ledger.set(res.data ?? []);
          this.ledgerTotalItems.set(res.totalItems ?? 0);
          this.ledgerTotalPages.set(res.totalPages ?? 0);
          this.ledgerLoading.set(false);
        },
        error: (err: unknown) => {
          this.ledger.set([]);
          this.ledgerTotalItems.set(0);
          this.ledgerTotalPages.set(0);
          this.ledgerLoading.set(false);
          this.ledgerError.set(
            toErrorMessage(err, "Impossible de charger l'historique."),
          );
        },
      });
  }

  goToLedgerPage(p: number): void {
    if (p < 1 || p > this.ledgerTotalPages() || p === this.ledgerPage()) return;
    this.ledgerPage.set(p);
    this.loadLedger();
  }
}
